<template>
  <header
    class="tw-flex tw-h-16 tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-border-b tw-border-neutral-200 tw-bg-white dark:tw-border-neutral-800 dark:tw-bg-neutral-950 tw-relative tw-z-50"
  >
    <!-- Left side - Sidebar trigger and breadcrumbs -->
    <div class="tw-flex tw-items-center tw-gap-2 tw-px-2">
      <SidebarTrigger class="tw-ml-1" />
      <Separator orientation="vertical" class="tw-mr-2 tw-h-4" />
      <Breadcrumb class="tw-flex tw-items-center tw-gap-2 tw-mt-4">
        <BreadcrumbList>
          <BreadcrumbItem class="tw-hidden md:tw-block">
            <BreadcrumbLink as-child>
              <RouterLink :to="{ name: 'dashboard' }">Inocu</RouterLink>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <template v-if="breadcrumbs.length > 0">
            <BreadcrumbSeparator class="tw-hidden md:tw-block" />
            <template v-for="(item, index) in breadcrumbs" :key="index">
              <BreadcrumbItem v-if="index > 0">
                <BreadcrumbSeparator />
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink v-if="item.to && index < breadcrumbs.length - 1" as-child>
                  <RouterLink :to="item.to">{{ item.label }}</RouterLink>
                </BreadcrumbLink>
                <BreadcrumbPage v-else>{{ item.label }}</BreadcrumbPage>
              </BreadcrumbItem>
            </template>
          </template>
        </BreadcrumbList>
      </Breadcrumb>
    </div>

    <!-- Right side - Customer dropdown, Language dropdown, Notifications, User dropdown -->
    <div class="tw-flex tw-items-center tw-gap-4 tw-min-w-0 tw-shrink">
      <!-- Customer Selection Dropdown -->
      <Combobox
        v-if="assignedCustomersList && assignedCustomersList.length > 0"
        v-model="selectedCustomerForCombobox"
        @update:model-value="handleCustomerSelect"
        :display-value="(customer: any) => customer?.customerName || ''"
        class="tw-min-w-0 tw-shrink"
      >
        <div class="tw-relative">
          <ComboboxAnchor>
            <ComboboxTrigger as-child>
              <Button role="combobox" variant="secondary" class="tw-w-full tw-justify-between">
                <span class="tw-truncate">
                  {{ selectedCustomerName || t('users.titles.select_customer') }}
                </span>
                <ChevronDown class="tw-ml-2 tw-h-4 tw-w-4" />
              </Button>
            </ComboboxTrigger>
          </ComboboxAnchor>

          <ComboboxList
            position="popper"
            class="tw-w-full tw-min-w-[var(--radix-combobox-trigger-width)] tw-p-1 tw-z-[150] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
          >
            <div class="tw-p-2">
              <ComboboxInput
                :placeholder="t('common.data.search')"
                class="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500 focus:tw-border-transparent"
              />
            </div>

            <div class="tw-max-h-60 tw-overflow-y-auto">
              <ComboboxEmpty class="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500">
                {{ t('common.data.no_data') }}
              </ComboboxEmpty>

              <ComboboxItem
                v-for="customer in visibleCustomersList"
                :key="customer.customerId"
                :value="customer"
                class="tw-relative tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-justify-between tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-outline-none data-[highlighted]:tw-bg-accent data-[highlighted]:tw-text-accent-foreground hover:tw-bg-gray-100"
              >
                <span class="tw-font-medium tw-truncate">{{ customer.customerName }}</span>
                <Badge
                  v-if="!customer.isCustomerActive"
                  variant="custom"
                  class="tw-shrink-0 tw-text-[10px] tw-bg-secondary-100 tw-text-secondary-600"
                >
                  {{ t('customer_management.titles.archived') }}
                </Badge>
              </ComboboxItem>
            </div>

            <div
              v-if="hasArchivedCustomers || isAdminUser"
              class="tw-border-t tw-border-neutral-100 tw-mt-1 tw-pt-1"
            >
              <button
                type="button"
                :disabled="isLoadingArchived"
                @mousedown.prevent="toggleArchived"
                class="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-2 tw-py-1.5 tw-text-sm tw-font-medium tw-text-secondary-600 hover:tw-bg-gray-100 tw-rounded-sm disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
              >
                <Loader2 v-if="isLoadingArchived" class="tw-h-4 tw-w-4 tw-animate-spin" />
                <Archive v-else class="tw-h-4 tw-w-4" />
                <span>
                  {{
                    showArchived ? t('users.titles.hide_archived') : t('users.titles.load_archived')
                  }}
                </span>
              </button>
            </div>
          </ComboboxList>
        </div>
      </Combobox>

      <!-- Language Dropdown -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon">
            <Globe class="tw-h-4 tw-w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="tw-z-[150] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
        >
          <DropdownMenuItem @click="changeLanguage('en')" class="tw-cursor-pointer">
            <span class="tw-font-medium">English</span>
          </DropdownMenuItem>
          <DropdownMenuItem @click="changeLanguage('de')" class="tw-cursor-pointer">
            <span class="tw-font-medium">Deutsch</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- Notifications Dropdown -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon" class="tw-relative">
            <Bell class="tw-h-4 tw-w-4" />
            <Badge
              variant="custom"
              v-if="notReadNotificationsCount > 0"
              class="tw-absolute -tw-top-2 -tw-right-2 tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-bg-red-200 tw-text-red-600 tw-rounded-full"
            >
              {{ notReadNotificationsCount }}
            </Badge>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="tw-w-80 tw-z-[150] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
          align="end"
        >
          <div class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-border-b">
            <span class="tw-font-semibold">{{ t('notifications.titles.notifications') }}</span>
            <Button
              v-if="notReadNotificationsCount > 0"
              variant="ghost"
              size="sm"
              @click="markAllAsRead()"
              class="tw-text-sm"
            >
              {{ t('notifications.titles.mark_all_read') }}
            </Button>
          </div>

          <div class="tw-max-h-80 tw-overflow-y-auto">
            <template v-if="notificationsListInDropdown.length">
              <DropdownMenuItem
                v-for="notification in notificationsListInDropdown.slice(0, 4)"
                :key="notification.id"
                @click="performNotificationAction(notification)"
                class="tw-cursor-pointer tw-px-2 tw-py-2 tw-border-b tw-border-neutral-100 dark:tw-border-neutral-800 tw-gap-2"
                style="min-height: 56px"
              >
                <div class="tw-flex tw-items-start tw-gap-2 tw-w-full">
                  <div
                    class="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center"
                  >
                    <FileText class="tw-h-4 tw-w-4 tw-text-secondary-500" />
                  </div>
                  <div class="tw-flex-1 tw-min-w-0">
                    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
                      <h4
                        class="tw-font-medium tw-text-sm tw-truncate"
                        :title="notification.title"
                        style="max-width: 160px"
                      >
                        {{ notification.title }}
                      </h4>
                      <span
                        class="tw-text-xs tw-text-neutral-400 tw-whitespace-nowrap tw-ml-2"
                        style="min-width: 70px; text-align: right"
                      >
                        {{ moment(notification.created_on).fromNow() }}
                      </span>
                    </div>
                    <p
                      class="tw-text-xs tw-text-muted-foreground tw-mt-0.5 tw-line-clamp-1"
                      :title="notification.text"
                    >
                      {{ notification.text }}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            </template>

            <div v-else class="tw-p-4 tw-text-center tw-text-muted-foreground tw-text-sm">
              {{ t('notifications.data.no_data') }}
            </div>
          </div>

          <div class="tw-p-2 tw-border-t">
            <Button
              variant="ghost"
              size="sm"
              @click="$router.push({ name: 'notification-center' })"
              class="tw-w-full tw-justify-center tw-text-secondary-500 tw-font-semibold tw-text-base hover:tw-bg-transparent"
            >
              {{ t('common.data.view_all') }}
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- User Dropdown -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" class="tw-flex tw-items-center tw-gap-2 tw-h-10">
            <Avatar class="tw-h-8 tw-w-8 tw-rounded-full">
              <AvatarImage
                v-if="currentUser?.profile_picture"
                :src="currentUser?.profile_picture"
                :alt="t('profile.titles.user_image')"
              />
              <AvatarFallback v-else>
                {{ currentUser?.first_name?.charAt(0)?.toUpperCase() }}
                {{ currentUser?.last_name?.charAt(0)?.toUpperCase() }}
              </AvatarFallback>
            </Avatar>
            <div class="tw-hidden md:tw-flex tw-flex-col tw-items-start">
              <span class="tw-text-sm tw-font-medium tw-leading-none">
                {{ currentUser?.display_name }}
              </span>
              <span class="tw-text-[10px] tw-text-secondary-200 tw-leading-none tw-mt-1">
                {{ currentUser?.user_role.join(', ') }}
              </span>
            </div>
            <ChevronDown class="tw-ml-2 tw-h-4 tw-w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          class="tw-w-56 tw-z-[150] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
        >
          <DropdownMenuItem @click="$router.push({ name: 'my-profile' })" class="tw-cursor-pointer">
            <User class="tw-mr-2 tw-h-4 tw-w-4" />
            {{ t('common.nav.my_profile') }}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="logout" class="tw-cursor-pointer tw-text-red-600">
            <LogOut class="tw-mr-2 tw-h-4 tw-w-4" />
            {{ t('auth.titles.logout') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>

<script setup lang="ts">
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { ComboboxTrigger } from 'reka-ui';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';

// Icons
import {
  ChevronDown,
  Globe,
  Bell,
  FileText,
  User,
  LogOut,
  Archive,
  Loader2,
} from 'lucide-vue-next';

import { useAuthStore } from '@/stores/auth.store';
import { useUserStore } from '@/stores/user.store';
import { useCustomerStore } from '@/stores/customer.store';
import { onMounted, onUnmounted, ref, computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { UserRoles } from '@/_helpers/interfaces';
import router from '@/router';
import { useI18n } from 'vue-i18n';
import { useNotificationsStore } from '@/stores/notifications.store';
import moment from 'moment';
import type { Notification } from '@/_helpers/interfaces/notifications/notification.interface';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { RouterLink } from 'vue-router';
import cookieService from '@/_helpers/services/cookie.service';
import broadcastService from '@/_helpers/services/broadcast.service';

const { t, locale } = useI18n();
const { breadcrumbs } = useBreadcrumbs();

const { logout } = useAuthStore();
const { currentUser } = storeToRefs(useUserStore());
const { getAssignedCustomersWithRolesList } = useUserStore();
const {
  getCustomersList,
  setCurrentSelectedCustomer,
  getCustomerById,
  setHasCustomers,
  setSelectedCustomerId,
} = useCustomerStore();

type AssignedCustomer = {
  customerId: number;
  customerName: string;
  roles: string[];
  isCustomerActive: boolean;
};

const assignedCustomersList: Ref<Array<AssignedCustomer> | null> = ref(null);
const selectedCustomer: Ref<number | null> = ref(null);
const selectedCustomerForCombobox: Ref<any> = ref(null);
const showArchived = ref(false);
const isLoadingArchived = ref(false);
const archivedLoaded = ref(false);

const isAdminUser = computed(() => currentUser.value?.user_role.includes(UserRoles.ADMIN) ?? false);

const hasArchivedCustomers = computed(
  () => assignedCustomersList.value?.some(c => !c.isCustomerActive) ?? false
);

const visibleCustomersList = computed(() => {
  if (!assignedCustomersList.value) return [];
  if (showArchived.value) return assignedCustomersList.value;
  return assignedCustomersList.value.filter(
    c => c.isCustomerActive || c.customerId === selectedCustomer.value
  );
});

const { notReadNotificationsCount, notificationsListInDropdown } = storeToRefs(
  useNotificationsStore()
);
const { markAsRead, markAllAsRead, getNotificationsList } = useNotificationsStore();

const selectedCustomerName = computed(() => {
  if (!selectedCustomer.value || !assignedCustomersList.value) return null;
  const customer = assignedCustomersList.value.find(c => c.customerId === selectedCustomer.value);
  return customer?.customerName || null;
});

onMounted(async () => {
  if (
    currentUser.value?.user_role.length &&
    !currentUser.value?.user_role.includes(UserRoles.ADMIN)
  ) {
    assignedCustomersList.value = getAssignedCustomersWithRolesList(currentUser.value);
    selectedCustomer.value = +cookieService.get('selectedCustomerId')!;
  } else if (currentUser.value?.user_role.includes(UserRoles.ADMIN)) {
    const resp = await getCustomersList({
      page_size: 100,
      is_active: true,
    });
    assignedCustomersList.value = resp.results.map(customer => ({
      customerId: customer.id,
      customerName: customer.name,
      roles: ['ADMIN'],
      isCustomerActive: customer.is_active,
    }));
  }

  // Update hasCustomers state in store for other components to use
  setHasCustomers(!!assignedCustomersList.value?.length);

  if (cookieService.get('selectedCustomerId')) {
    selectedCustomer.value = +cookieService.get('selectedCustomerId')!;
  } else if (assignedCustomersList.value?.length) {
    selectedCustomer.value = assignedCustomersList.value[0].customerId;
    cookieService.setSessionCookie('selectedCustomerId', `${selectedCustomer.value}`);

    // Check if we're in the middle of a tenant switch - if so, don't reload again
    // The tenant switch already triggered a reload, we just needed to set the customer ID
    if (cookieService.get('tenantSwitchInProgress')) {
      cookieService.remove('tenantSwitchInProgress');
    } else {
      location.reload();
    }
  }

  // Update the store with the selected customer ID for reactive components
  setSelectedCustomerId(selectedCustomer.value);

  if (selectedCustomer.value) {
    const customerData = await getCustomerById(selectedCustomer.value);
    setCurrentSelectedCustomer(customerData);

    // Ensure the currently selected customer is in the list, even if archived —
    // otherwise the trigger label and filtered list would drop it.
    if (
      customerData &&
      assignedCustomersList.value &&
      !assignedCustomersList.value.some(c => c.customerId === customerData.id)
    ) {
      assignedCustomersList.value = [
        ...assignedCustomersList.value,
        {
          customerId: customerData.id,
          customerName: customerData.name,
          roles: isAdminUser.value ? ['ADMIN'] : [],
          isCustomerActive: customerData.is_active,
        },
      ];
    }
  }

  await getNotificationsNavbarList();

  await getNotReadNotificationsCount();

  // Listen for customer changes in other tabs
  broadcastService.onCustomerChanged(customerId => {
    console.log(`Customer changed in another tab to ${customerId}. Reloading...`);
    location.reload();
  });

  // Listen for logout events in other tabs
  broadcastService.onLogout(() => {
    console.log('Logout detected in another tab. Redirecting to login...');
    location.reload();
  });
});

const getNotificationsNavbarList = async () => {
  const response = await getNotificationsList({
    page: 1,
    page_size: 5,
    ordering: '-created_on',
  });

  notificationsListInDropdown.value = [...response.results];
};

const getNotReadNotificationsCount = async () => {
  const { count } = await getNotificationsList({
    read: false,
  });

  notReadNotificationsCount.value = count;
};

const performNotificationAction = async (notification: Notification) => {
  markAsRead(notification.id, false);

  const routeName =
    notification.object_ct === 'purchaseorder'
      ? 'order'
      : notification.object_ct === 'payment'
      ? 'payment'
      : '';

  if (routeName)
    router.push({
      name: routeName,
      params: {
        id: notification.object_id,
      },
    });
  else
    router.push({
      name: 'notification-center',
    });
};

const onSelectCustomer = async (customerId: any) => {
  if (customerId) {
    // Update cookie and store
    cookieService.setSessionCookie('selectedCustomerId', `${customerId}`);
    setSelectedCustomerId(customerId);

    // Broadcast customer change to other tabs
    broadcastService.notifyCustomerChanged(customerId);

    const userRoles = currentUser.value?.user_role;

    if (!userRoles?.length) {
      router.push({ name: 'my-profile' });
    } else {
      router.push({ name: 'home' });
    }

    // Reload current tab after routing
    setTimeout(() => {
      location.reload();
    }, 100);
  }
};

const toggleArchived = async () => {
  if (showArchived.value) {
    showArchived.value = false;
    return;
  }

  if (isAdminUser.value && !archivedLoaded.value) {
    try {
      isLoadingArchived.value = true;
      const resp = await getCustomersList({
        page_size: 100,
        is_active: false,
      });
      const archived = resp.results
        .filter(c => !assignedCustomersList.value?.some(existing => existing.customerId === c.id))
        .map(customer => ({
          customerId: customer.id,
          customerName: customer.name,
          roles: ['ADMIN'],
          isCustomerActive: customer.is_active,
        }));
      assignedCustomersList.value = [...(assignedCustomersList.value ?? []), ...archived];
      archivedLoaded.value = true;
    } finally {
      isLoadingArchived.value = false;
    }
  }

  showArchived.value = true;
};

const handleCustomerSelect = (customer: any) => {
  if (customer && customer.customerId !== selectedCustomer.value) {
    onSelectCustomer(customer.customerId);
  }
  // Reset combobox selection to allow reselecting
  setTimeout(() => {
    selectedCustomerForCombobox.value = null;
  }, 100);
};

const changeLanguage = (lang: string) => {
  if (locale.value === lang) return;

  const currentRoute = router.currentRoute.value;

  router.push({
    ...currentRoute,
    // @ts-ignore
    params: {
      ...currentRoute.params,
      locale: lang,
    },
  });
};
</script>
